import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdKeyboardArrowDown, MdKeyboardArrowRight, MdForum, MdAssessment } from 'react-icons/md';
import { researchosService } from '../../services/researchosService';
import PrimerReader from './company/PrimerReader';
import { PoolSection, METRICS } from './TeamScoresPage';   // reuse Quorum's pool→param→sub drill-down

// ResearchOS → company profile (the read side of the search-first surface). One screen assembling
// everything held for a name: public chatter, the analyst primer (rendered, not "attached"), and
// the full scorecard through the SAME PoolSection Quorum uses. Each panel SELF-HIDES when its data
// is absent (a name can be scored before its notebook is written) so an empty panel is never a lie.

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const num = (x, dp = 2) => (x == null || Number.isNaN(Number(x)) ? '—' : Number(x).toFixed(dp));
const signColor = (x) => (x == null ? 'text-gray-500' : Number(x) > 0 ? 'text-green-500' : Number(x) < 0 ? 'text-red-500' : 'text-gray-400');

// Collapsible section. NO overflow-hidden — it would become a containing block for the primer's
// sticky chapter nav and silently break it (GreenSkew's hard-won note).
function Section({ title, subtitle, icon: Icon, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              className="flex w-full items-center gap-2 rounded-t-xl px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40">
        <span className="grid w-5 shrink-0 place-items-center text-gray-400">{open ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}</span>
        {Icon ? <Icon className="shrink-0 text-brand-gold" size={18} /> : null}
        <span className="min-w-0 flex-1">
          <span className="font-serif text-lg text-gray-900 dark:text-gray-100">{title}</span>
          {subtitle ? <span className="ml-2 text-[11px] text-gray-400">{subtitle}</span> : null}
        </span>
      </button>
      {open && <div className="border-t border-gray-100 p-4 dark:border-gray-800/60">{children}</div>}
    </section>
  );
}

function ChatterBlock({ chatter }) {
  if (!chatter) return null;
  const Tile = ({ label, children }) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900/40">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-0.5 font-mono text-sm text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="Favorability"><span className={signColor(chatter.favorability)}>{num(chatter.favorability)}</span></Tile>
        <Tile label="Momentum"><span className={signColor(chatter.momentum)}>{num(chatter.momentum)}</span></Tile>
        <Tile label="Mentions">{chatter.mentions ?? '—'} <span className="text-gray-500">({chatter.mentions_recent ?? 0} recent)</span></Tile>
        <Tile label="Coverage">{chatter.coverage || '—'}</Tile>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <span className="rounded bg-green-500/15 px-2 py-1 text-green-500">Bull {chatter.bull ?? 0}</span>
        <span className="rounded bg-red-500/15 px-2 py-1 text-red-500">Bear {chatter.bear ?? 0}</span>
        <span className="rounded bg-gray-500/15 px-2 py-1 text-gray-400">Neutral {chatter.neutral ?? 0}</span>
      </div>
    </div>
  );
}

const LENS = { score: '0–5 assessment', weight: 'decision weight within pool', contribution: 'score × weight' };

function ScoresPanel({ scores }) {
  const [mode, setMode] = useState('score');
  const primary = scores[0];
  const pools = Array.isArray(primary.pool_scores) ? primary.pool_scores : [];
  const all = Array.isArray(primary.parameters) ? primary.parameters : [];
  const params = all.filter((p) => p.parent_key == null);
  const subs = all.filter((p) => p.parent_key != null);
  const others = scores.slice(1);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-gray-400">
          {primary.scored_by_name || 'Team'}{primary.method_version ? ` · ${primary.method_version}` : ''} · {fmtDate(primary.known_at)}
          {primary.overall_score_final != null && <span className="ml-2 text-gray-500">overall <span className="font-mono text-brand-gold">{num(primary.overall_score_final)}</span></span>}
        </div>
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs dark:border-gray-800">
            {METRICS.map((mt) => (
              <button key={mt.key} type="button" onClick={() => setMode(mt.key)}
                      className={`rounded-md px-2.5 py-1 font-medium transition-colors ${mode === mt.key ? 'bg-brand-gold text-black' : 'text-gray-500'}`}>
                {mt.label}
              </button>
            ))}
          </div>
          <span className="hidden text-[11px] text-gray-400 sm:inline">{LENS[mode]}</span>
        </div>
      </div>
      {pools.length ? (
        <div className="flex flex-col gap-4">
          {pools.map((pool, i) => <PoolSection key={i} pool={pool} params={params} subs={subs} mode={mode} />)}
        </div>
      ) : (
        <p className="text-xs text-gray-500">This scorecard has no pool breakdown.</p>
      )}
      {others.length > 0 && (
        <p className="mt-3 text-[11px] text-gray-400">
          Also scored by {others.map((o) => o.scored_by_name).filter(Boolean).join(', ')} — showing the most recent.
        </p>
      )}
    </div>
  );
}

export default function CompanyPage() {
  const { ticker } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');   // loading | ok | notfound | error

  useEffect(() => {
    let alive = true;
    setStatus('loading'); setData(null);
    researchosService.getCompanyProfile(ticker)
      .then((r) => { if (alive) { setData(r.data); setStatus('ok'); } })
      .catch((e) => { if (alive) setStatus(e?.response?.status === 404 ? 'notfound' : 'error'); });
    return () => { alive = false; };
  }, [ticker]);

  const Crumb = () => (
    <button onClick={() => nav('/researchos')} className="mb-4 flex items-center gap-1 text-[11px] text-gray-400 transition-colors hover:text-brand-gold">
      <MdArrowBack size={14} /> ResearchOS
    </button>
  );

  if (status === 'loading') return <div><Crumb /><div className="p-8 text-center text-sm text-gray-500">Loading {(ticker || '').toUpperCase()}…</div></div>;
  if (status !== 'ok' || !data) {
    const notfound = status === 'notfound';
    return (
      <div>
        <Crumb />
        <div className="rounded-xl border border-gray-200 p-8 text-center dark:border-gray-800">
          <h1 className="font-serif text-xl text-gray-900 dark:text-gray-100">{(ticker || '').toUpperCase()}</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {notfound ? 'No research is held for this name yet.' : 'That could not be loaded — please try again.'}
          </p>
        </div>
      </div>
    );
  }

  const scores = Array.isArray(data.scores) ? data.scores : [];
  return (
    <div className="space-y-4">
      <Crumb />
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-gray-200 pb-3 dark:border-gray-800">
        <span className="font-mono text-2xl font-semibold text-gray-900 dark:text-gray-100">{data.ticker}</span>
        {data.security_name ? <span className="text-gray-600 dark:text-gray-300">{data.security_name}</span> : null}
        {(data.sector || data.industry) ? <span className="text-xs text-gray-500">{[data.sector, data.industry].filter(Boolean).join(' · ')}</span> : null}
      </div>

      {data.chatter ? <Section title="Public chatter" icon={MdForum}><ChatterBlock chatter={data.chatter} /></Section> : null}
      {data.notebook ? <Section title="Primer"><PrimerReader notebook={data.notebook} /></Section> : null}
      {scores.length ? (
        <Section title="Scorecard" icon={MdAssessment} subtitle={`${scores.length} team${scores.length > 1 ? 's' : ''}`}>
          <ScoresPanel scores={scores} />
        </Section>
      ) : (
        <div className="rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-gray-800">
          No scorecards you can see for this name{data.chatter ? ' — chatter only.' : '.'}
        </div>
      )}
    </div>
  );
}
